import { toast } from "react-toastify";
import { apiClient } from "../../../api/httpClient";
import { extractErrorMessage } from "../../../globals/utils/error-handler";
import logger from "../../../globals/utils/logger";
import { buildProbateSplitPayload } from "../shared/bondPayloadBuilders";
import { parseCurrencyToNumber, safeNumber } from "../shared/formHelpers";
import {
  runDuplicateBondCheck,
  runPostCreateBondCleanup,
} from "../shared/bondFormCreationHelpers";

export async function checkLocationCustomerNumber(lawFirmId, lawFirmLocationId) {
  try {
    if (!lawFirmId || !lawFirmLocationId) return false;
    const resp = await apiClient(
      `/check-location-customer-number/${lawFirmId}/${lawFirmLocationId}`,
      "GET"
    );
    return resp?.result === true;
  } catch (err) {
    logger.debug("useProbateBondForm", "checkLocationCustomerNumber failed", { err });
    return false;
  }
}

/**
 * Probate-only create path: optional auto-approve for small bonds.
 * @param {import("../../shared/bondFormConfig").BondFormKindConfig} config
 * @param {import("../../shared/hooks/useBondFormBase").BondFormBaseContext} ctx
 */
export async function createProbateBondWithAutoApprove(config, ctx, payload, options = {}) {
  const { skipAutoApprove = false, finalizeAfterCreate = true } = options;
  const {
    isGuestRequest,
    setIsLoading,
    setSubmissionError,
    setFailedBondType,
    setSupportModalOpen,
  } = ctx;

  let response;
  try {
    response = await apiClient(config.createEndpoint, "POST", payload);
    const approvePayload = {
      ...response,
      bondNumber: response?.bond_number?.bondno_id,
      billing_method: "Direct Bill",
    };
    const bondAmount = safeNumber(parseCurrencyToNumber(response?.bond_amount));
    const underwritingCreated = response?.is_underwriting_created === true;
    const isCuratorBond = payload.bond_sub_category === "Curator";

    if (
      bondAmount <= 18000 &&
      !isGuestRequest &&
      !skipAutoApprove &&
      !underwritingCreated &&
      !isCuratorBond
    ) {
      const eligible = await checkLocationCustomerNumber(
        payload.law_firm,
        payload.law_firm_location
      );
      if (eligible) {
        await apiClient(`/update_probate_bond/${response.bond_id}/`, "PATCH", {
          billing_method: "Direct Bill",
          probatebond: { billing_method: "Direct Bill" },
        });
        await apiClient(`/approve_probate_bond/${response.bond_id}/`, "POST", approvePayload);
      }
    }
  } catch (error) {
    const message = extractErrorMessage(error);
    setSubmissionError(message);
    setFailedBondType(config.failedBondType);
    setSupportModalOpen(true);
    if (finalizeAfterCreate) setIsLoading(false);
    return;
  }

  if (!finalizeAfterCreate) {
    return;
  }

  setIsLoading(false);
  localStorage.removeItem("guestReferralData");
  toast.success("Bond created successfully!");

  try {
    await runPostCreateBondCleanup(ctx, response, "useProbateBondForm");
  } catch (error) {
    logger.warn("useProbateBondForm", "post-create cleanup failed after successful bond create", {
      err: error,
    });
  }
}

/**
 * @param {import("../../shared/bondFormConfig").BondFormKindConfig} config
 * @param {import("../../shared/hooks/useBondFormBase").BondFormBaseContext} ctx
 */
export async function submitSplitBondsByRepresentative(
  config,
  ctx,
  basePayload,
  principalCount,
  createBond
) {
  const {
    globalState,
    setDuplicateBondId,
    setPendingPayload,
    setIsDuplicateDialogOpen,
    setIsLoading,
  } = ctx;

  try {
    const guardians = Array.isArray(globalState.guardianDetails?.guardians)
      ? globalState.guardianDetails.guardians
      : [];
    const count = Math.min(principalCount, guardians.length);
    for (let i = 0; i < count; i++) {
      const rep = guardians[i];
      if (!rep) continue;
      const splitPayload = buildProbateSplitPayload(basePayload, rep);
      try {
        const dupResp = await apiClient(config.duplicateCheck.endpoint, "POST", {
          bond_data: splitPayload,
          probatebond: splitPayload.probatebond,
          personal_rep: splitPayload.personal_rep,
        });
        if (dupResp?.duplicate) {
          setDuplicateBondId(dupResp?.bond_id || null);
          setPendingPayload(splitPayload);
          setIsDuplicateDialogOpen(true);
          setIsLoading(false);
          return true;
        }
      } catch (err) {
        logger.debug("useProbateBondForm", "split bond duplicate check failed", { err });
      }
      await createBond(splitPayload, { skipAutoApprove: true, finalizeAfterCreate: i === count - 1 });
    }
  } catch (err) {
    logger.warn("useProbateBondForm", "submitSplitBondsByRepresentative failed", { err });
    setIsLoading(false);
  }
  return false;
}

/** @param {import("../../shared/hooks/useBondFormBase").BondFormBaseContext} ctx */
export async function submitSingleProbateBond(ctx, payload, createBond) {
  const isDuplicate = await runDuplicateBondCheck(ctx, payload);
  if (!isDuplicate) {
    await createBond(payload);
  }
}
